import React, { useState } from 'react';
import { resolveAvatarUrl } from '../../utils/api';

/**
 * Kanonischer Avatar des Design-Systems.
 *
 * Das Audit fand **acht** konkurrierende Platzhalter-Strategien: zwei
 * verschiedene Verlaufskreise, ein Token-Kreis, ein Hash auf sechs flache
 * Farben, lucide-Icons, ein statisches KI-Otter-Bild — und 26 Aufrufe an den
 * Fremddienst `ui-avatars.com` mit vier hartkodierten Hintergrundfarben
 * (einer davon `background=random`, wodurch derselbe Nutzer je nach Antwort
 * die Farbe wechselte).
 *
 * Seit Aug 2026 gibt es genau EINEN Platzhalter (Marken-Look statt Zufall),
 * seit Okt 2026 so wie im Werbespot und in den E-Mails:
 *
 *   1. Flache Marken-Tint-Flaeche (`--color-primary-soft`), kein Verlauf.
 *   2. GROSSE Initialen in Bungee (`--font-display`) in CTA-Lila.
 *
 * Und genau EINEN Rahmen (`frame`, Okt 2026): 2px Schnitt in der Flaechenfarbe
 * plus 1px Haarlinie aussen (index.css `.avatar-frame`). Vorher setzte jede
 * Aufrufstelle ihren eigenen Rand — fuenf Varianten, keine wie im Spot. Wer
 * einen Avatar in eine Huelle mit Rand und Schatten steckt, macht das jetzt
 * nicht mehr: Groesse waehlen, Rahmen kommt mit.
 *
 * Die Initialen stehen in einem SVG mit `viewBox="0 0 100 100"`. Dadurch
 * skaliert die Schrift automatisch mit dem Kreis: im 24px-Listeneintrag
 * genauso proportional wie im 144px-Kopf der Match-Karte. Vorher hing die
 * Groesse an einer festen Tailwind-Klasse — deshalb sassen auf der grossen
 * Match-Karte winzige Buchstaben in einem grossen Kreis.
 *
 * Alles wird **lokal und deterministisch** erzeugt: kein Netzwerkaufruf an
 * Dritte. Fuer eine Schul-App ist das auch datenschutzseitig die richtige
 * Variante, weil sonst pro fehlendem Bild der Name an einen Fremdserver geht.
 *
 * - name:    Anzeigename (liefert die Initialen)
 * - src:     Bildpfad; wird ueber resolveAvatarUrl() aufgeloest
 * - size:    2xs (20) | xs (24) | sm (32) | md (40) | row (44) | lg (56)
 *            | xl (96) | 2xl (128) | fill
 * - shape:   circle (Standard) | square (abgerundetes Quadrat, z.B. Listen)
 * - frame:   default (Schnitt + Haarlinie) | thick (3px-Ring, grosse Fotos)
 *            | none (z.B. Bild-Vorschau im Upload)
 * - variant: Bildvariante fuer resolveAvatarUrl ('thumb' | 'card')
 */

const SIZES = {
  '2xs': 'w-5 h-5',   // Mini-Stapel in Meta-Zeilen (Logbuch)
  xs: 'w-6 h-6',
  sm: 'w-8 h-8',      // Stapel auf Session-Karten
  md: 'w-10 h-10',    // Chat-Kopf, Personenzeilen
  row: 'w-11 h-11',   // Trefferzeilen (Suche, Chatliste) — Kit .eo-row
  lg: 'w-14 h-14',
  xl: 'w-24 h-24',
  '2xl': 'w-32 h-32', // Profilfoto der Profilkarte
  // 'fill' fuer die verbreitete Bauweise "Bild in fester Huelle": der Eltern-
  // Container bringt Groesse, Rand und Schatten mit, der Avatar fuellt ihn aus.
  // Ohne diese Stufe muesste die Groesse per className ueberschrieben werden —
  // das ist bei Tailwind unzuverlaessig (gleiche Spezifitaet, Reihenfolge im
  // erzeugten CSS entscheidet, nicht die Reihenfolge im class-Attribut).
  fill: 'w-full h-full',
};

const SHAPES = {
  circle: 'rounded-full',
  square: 'rounded-xl',
};

const FRAMES = {
  default: 'avatar-frame',
  thick: 'avatar-frame-thick',
  none: '',
};

const initialsOf = (name = '') => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

// Schriftgroesse in viewBox-Einheiten (= Prozent des Kreisdurchmessers).
// Bungee ist eine breite Schrift, zwei Buchstaben brauchen deutlich weniger.
const fontSizeFor = (initials) => (initials.length > 1 ? 44 : 58);

const Avatar = ({
  name = '',
  src,
  size = 'md',
  shape = 'circle',
  frame = 'default',
  variant = 'thumb',
  className = '',
  // Bild-only-Attribute: auf dem Platzhalter-<div> waeren sie ungueltiges HTML
  // (React-Warnung), deshalb hier herausgeloest statt in ...rest.
  loading,
  decoding,
  ...rest
}) => {
  const [failed, setFailed] = useState(false);
  const resolved = src && !failed ? resolveAvatarUrl(src, variant) : '';
  const shapeClass = SHAPES[shape] || SHAPES.circle;
  const frameClass = FRAMES[frame] ?? FRAMES.default;
  const base = [SIZES[size] || SIZES.md, shapeClass, frameClass, 'shrink-0 object-cover', className]
    .filter(Boolean)
    .join(' ');

  if (resolved) {
    return (
      <img
        src={resolved}
        alt={name ? `Profilbild von ${name}` : 'Profilbild'}
        onError={() => setFailed(true)}
        loading={loading}
        decoding={decoding}
        className={base}
        {...rest}
      />
    );
  }

  const initials = initialsOf(name);

  return (
    <div
      role="img"
      aria-label={name ? `Profilbild von ${name}` : 'Profilbild'}
      className={`${base} avatar-placeholder flex items-center justify-center select-none`}
      {...rest}
    >
      <svg
        viewBox="0 0 100 100"
        className="w-full h-full"
        aria-hidden="true"
        focusable="false"
      >
        <text
          x="50"
          y="50"
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={fontSizeFor(initials)}
          className="avatar-placeholder-initials"
        >
          {initials}
        </text>
      </svg>
    </div>
  );
};

export default Avatar;
